import _get from 'lodash/get'

import {
    CURRENCY,
    DEFAULT_LOCALE,
    ERROR_MESSAGE_UPLOADING_FILE,
    FILE_TYPE_TITLES,
    FILE_TYPE_WITH_SUBTYPE_TITLES,
} from '../constants'
import { pipe } from './fp'

export const hexToRgbA = (hex, opacity = 1) => {
    let c
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('')
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]]
        }
        c = `0x${c.join('')}`

        return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},${opacity})`
    }
    throw new Error('Bad Hex')
}

const withoutSpaces = (string) => string.replace(/\s/g, '')
const withoutMeaninglessDecimals = (string) => string.replace(',00', '')
const withoutSpacesAndWithoutMeaninglessDecimals = pipe(withoutSpaces, withoutMeaninglessDecimals)

const getCurrencySymbol = (currency) => {
    const CURRENCIES_SYMBOLS = {
        EUR: '€',
        GBP: '£',
        USD: '$',
    }
    return CURRENCIES_SYMBOLS[currency] ?? currency
}

const currencyFormatter =
    (formatter) =>
    (value, { currency = CURRENCY } = {}) => {
        let intlFormatter
        try {
            intlFormatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
                style: 'currency',
                currency,
                currencyDisplay: 'narrowSymbol',
            })
        } catch {
            intlFormatter = {
                format: (number) => `${(number ?? 0).toFixed(2).replace('.', ',')}${getCurrencySymbol(currency)}`,
            }
        }
        return formatter(intlFormatter.format(value))
    }

export const formatCurrency = currencyFormatter(withoutSpacesAndWithoutMeaninglessDecimals)
export const formatCurrencyWithDecimals = currencyFormatter(withoutSpaces)
export const formatCurrencyRounded = (value, options) => formatCurrency((value ?? 0).toFixed(1), options)

const percentageFormatter = (value) => {
    const intlFormatter = new Intl.NumberFormat('es-ES', { style: 'percent', maximumFractionDigits: 2 })
    return intlFormatter.format(value / 100)
}

export const formatPercentage = (string) => percentageFormatter(string || 0)

export const makeNounNumberFormatter =
    ({ defaultIfZero } = {}) =>
    (number, { singular, plural = `${singular}s` }) => {
        if (!number) {
            return defaultIfZero || `0 ${plural}`
        }
        if (number === 1) {
            return `${number} ${singular}`
        }

        return `${number} ${plural}`
    }

export const pluralizeNounFormatter =
    ({ singular, plural = `${singular}s` }) =>
    (number) =>
        number === 1 ? singular : plural

export const getFileTypeWithSubtypeTitle = ({ fileType, fileSubType }) => {
    if (fileSubType) return _get(FILE_TYPE_WITH_SUBTYPE_TITLES, [fileType, fileSubType])
    return FILE_TYPE_TITLES[fileType]
}

export const truncateTextMiddle = (string, outputLength = 18, separator = '...') => {
    if (string.length <= outputLength) return string

    const charsToShow = outputLength - separator.length
    const frontChars = Math.ceil(charsToShow / 2)
    const backChars = Math.floor(charsToShow / 2)

    return string.substr(0, frontChars) + separator + string.substr(string.length - backChars)
}

export const formatFileUploadErrorMessage = (fileName) =>
    `${ERROR_MESSAGE_UPLOADING_FILE} ${fileName ? `“${truncateTextMiddle(fileName)}“` : ``}`

export const formatFileUploadSuccessMessage = (fileName) =>
    `El archivo ${fileName ? `"${truncateTextMiddle(fileName)}"` : ''} se ha subido correctamente`

export const formatStreetAddress = ({
    door = '',
    floor = '',
    number = '',
    stairs = '',
    street = '',
    streetType = '',
} = {}) => {
    const parts = [`${streetType}/${street}`]

    if (number) parts.push(`nº ${number}`)
    if (stairs) parts.push(`escalera ${stairs}`)
    if (floor) parts.push(`piso ${floor}`)
    if (door) parts.push(`puerta ${door}`)

    return parts.join(', ')
}

export const formatAlphaNumericalUppercase = (value) =>
    typeof value === 'string' ? value.replace(/[^A-Za-z0-9]/g, '').toUpperCase() : ''

const getChunkIndex = ({ size, index }) => Math.floor(index / size)
/**
 * @returns {(string: string) => Array<string>} Given a size `S`, returns a function that chops a string in as many chunks of size `S` as needed.
 */
export const chunkString = (size) => (string) =>
    [...string].reduce((chunks, character, index) => {
        const chunkIndex = getChunkIndex({ size, index })
        return [...chunks.slice(0, chunkIndex), `${chunks[chunkIndex] || ''}${character}`]
    }, [])
